import {
  Component,
  computed,
  HostBinding,
  Inject,
  inject,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import {
  GlobalStore,
  SessionStorageService,
} from '@maximizer/core/shared/data-access';
import { CDN_URL, UserRights } from '@maximizer/core/shared/domain';
import { InsightsService } from '@maximizer/core/shared/insights';
import { UI_LOADER } from '@maximizer/core/shared/ui/components/loader';
import {
  ComposeService,
  EntrySearchService,
  RelatedService,
} from '@maximizer/outlook/main/data-access';
import {
  ComposeStore,
  OutlookService,
  OutlookStore,
  TrackerService,
} from '@maximizer/outlook/shared/data-access';
import {
  EmailPosition,
  EntrySearchResult,
  EntryType,
  EventAction,
  EventJourney,
  EventMetadata,
  EventModule,
  OutlookEmail,
} from '@maximizer/outlook/shared/domain';
import {
  MenuItem,
  OUTLOOK_SHARED_UI,
  OutlookNotificationComponent,
} from '@maximizer/outlook/shared/ui';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { OutlookEntrySearchComponent } from '../../entry-search/entry-search.component';
import { OutlookComposeAddEntryComponent } from './add-entry/add-entry.component';
import { OutlookComposeSaveToEmailComponent } from './save-to-email/save-to-email.component';

@Component({
  selector: 'maximizer-outlook-compose',
  templateUrl: './compose.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    OutlookComposeSaveToEmailComponent,
    OutlookComposeAddEntryComponent,
    OutlookEntrySearchComponent,
    OUTLOOK_SHARED_UI,
    TranslatePipe,
    UI_LOADER,
  ],
})
export class OutlookMailComposeComponent implements OnInit, OnDestroy {
  @HostBinding('class.max-outlook') hostClass = true;

  saveToComponent = viewChild<OutlookComposeSaveToEmailComponent>(
    OutlookComposeSaveToEmailComponent,
  );
  notification =
    viewChild.required<OutlookNotificationComponent>('notification');
  recipientNotification = viewChild.required<OutlookNotificationComponent>(
    'recipientNotification',
  );

  private readonly relatedService = inject(RelatedService);

  EntryType = EntryType;
  relatedTitle = '';
  showRelatedSection = false;
  showSkeletonCardData = true;
  selectedEmail?: OutlookEmail;
  isMailboxSame = true;
  intervalSearchForNewEmail = 3000;
  intervalSearchSet = false;
  private fromEmailVerificationInterval?: ReturnType<typeof setInterval>;
  private destroyed = false;
  userRights?: UserRights;

  private readonly subscriptions = new Subscription();

  showEmptyStateMessage = computed(
    () =>
      !this.composeStore.isLoading() &&
      this.composeStore.inMaximizer().length === 0 &&
      this.composeStore.notInMaximizer().length === 0 &&
      this.composeStore.saveOnlyEntries().length === 0,
  );

  searchActive = signal(false);

  buildRecipientActions = (entry: EntrySearchResult): MenuItem[] => {
    const hasNoEmailAddress = !entry.emailAddress;
    const noEmailAddressReason = hasNoEmailAddress
      ? this.translate.instant('outlook.entrySearch.noEmailAddress')
      : undefined;

    return [
      {
        id: 'to',
        text: this.translate.instant('outlook.compose.search.to'),
        disabled: hasNoEmailAddress,
        title: noEmailAddressReason,
        click: () => void this.addRecipientToPosition(entry, 'to'),
      },
      {
        id: 'cc',
        text: this.translate.instant('outlook.compose.search.cc'),
        disabled: hasNoEmailAddress,
        title: noEmailAddressReason,
        click: () => void this.addRecipientToPosition(entry, 'cc'),
      },
      {
        id: 'bcc',
        text: this.translate.instant('outlook.compose.search.bcc'),
        disabled: hasNoEmailAddress,
        title: noEmailAddressReason,
        click: () => void this.addRecipientToPosition(entry, 'bcc'),
      },
      {
        id: 'saveOnly',
        text: this.translate.instant('outlook.compose.search.saveOnly'),
        click: () => void this.addRecipientToPosition(entry, 'saveOnly'),
      },
    ];
  };

  constructor(
    @Inject(CDN_URL) public readonly cdn: string,
    private readonly storage: SessionStorageService,
    private readonly outlookService: OutlookService,
    private readonly composeService: ComposeService,
    private readonly entrySearchService: EntrySearchService,
    private readonly translate: TranslateService,
    private readonly tracker: TrackerService,
    private readonly insightsService: InsightsService,
    public globalStore: GlobalStore,
    public outlookStore: OutlookStore,
    public composeStore: ComposeStore,
  ) {}

  ngOnInit(): void {
    this.initializeCompose();

    if (this.globalStore.session()?.user) {
      this.userRights = this.globalStore.session()?.user.rights;
      this.setRelatedSection();
    }
  }

  private setRelatedSection(): void {
    this.showRelatedSection = this.relatedService.canShowComposeRelatedSection(
      this.userRights,
    );

    if (this.showRelatedSection) {
      this.relatedTitle = this.relatedService.getRelatedTitle(this.userRights);
    }
  }

  navigateToRelated(): void {
    this.relatedService.navigateToRelated();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.subscriptions.unsubscribe();
    this.stopFromEmailVerification();
  }

  private initializeCompose(): void {
    this.subscriptions.add(
      this.composeService.recipientsClassified$.subscribe((inMaximizerEmails) =>
        this.saveToComponent()?.initialize(inMaximizerEmails),
      ),
    );

    this.subscriptions.add(
      this.composeService.composeCleared$.subscribe(() =>
        this.resetComponentProperties(),
      ),
    );

    this.subscriptions.add(
      this.composeService.loadErrors$.subscribe((error) =>
        this.showLoadError(error),
      ),
    );

    this.addTimerFromEmailVerification();
    this.composeService.startRecipientTracking();
  }

  resetComponentProperties(): void {
    this.selectedEmail = undefined;
    this.saveToComponent()?.resetComponentProperties();
  }

  async addRecipientToPosition(
    entry: EntrySearchResult,
    position: EmailPosition,
  ): Promise<void> {
    let success = false;

    try {
      this.composeStore.setLoading(true);

      const email = this.entrySearchService.toOutlookEmail(entry);
      email.position = position;

      if (position !== 'saveOnly') {
        await this.outlookService.addEmailToPosition(email, position);
      }

      this.handleEmailAddedFromSearch(email);
      this.recipientNotification().show('add', 'success', false);

      success = true;
    } catch (error) {
      this.recipientNotification().show('add', 'error');
      this.insightsService.trackException({ error: error as Error });
    } finally {
      this.composeStore.setLoading(false);
      this.tracker.trackEvent({
        module: EventModule.Compose,
        journey: EventJourney.AddRecipient,
        action: EventAction.Completed,
        metadata: {
          recipient: position,
          result: success ? EventMetadata.Success : EventMetadata.Failed,
        },
      });
    }
  }

  handleEmailAddedFromSearch(email: OutlookEmail): void {
    // Create unique localId using service method to ensure consistency
    email.localId = this.composeService.generateLocalId(email);

    if (email.position === 'saveOnly') {
      this.handleSaveOnlyEmailAdded(email);
    } else {
      this.handleRegularPositionEmailAdded(email);
    }
  }

  private handleSaveOnlyEmailAdded(email: OutlookEmail): void {
    const existsInMaximizer = this.composeStore
      .inMaximizer()
      .some(
        (existing) =>
          existing.emailAddress === email.emailAddress &&
          existing.position === email.position &&
          existing.id === email.id,
      );

    if (!existsInMaximizer) {
      const updatedInMaximizer = [...this.composeStore.inMaximizer(), email];
      this.composeStore.updateInMaximizer(updatedInMaximizer);
    }

    const emailForSaveOnly = { ...email };
    emailForSaveOnly.position = 'saveOnly';
    // Create unique localId for saveOnly entries using service method
    emailForSaveOnly.localId = this.composeService.generateLocalId(
      email,
      'saveOnly',
    );

    this.composeStore.addEmailToSaveOnly(emailForSaveOnly);
    this.composeStore.addEmailToSelected(emailForSaveOnly);
  }

  private handleRegularPositionEmailAdded(email: OutlookEmail): void {
    const updatedInMaximizer = [...this.composeStore.inMaximizer(), email];
    this.composeStore.updateInMaximizer(updatedInMaximizer);
    this.saveToComponent()?.addEntryToPosition(email);
  }

  refresh(): void {
    globalThis.location.reload();
  }

  private showLoadError(error: unknown): void {
    console.error('Err', error);
    this.notification().show('load', 'error');
  }

  private async addTimerFromEmailVerification() {
    if (this.outlookStore.isComposeIntervalDefined()) return;

    const firstEmail = await this.outlookService.getComposeFrom();
    if (this.destroyed || !firstEmail) return;

    this.outlookStore.setIsComposeIntervalDefined(true);
    this.fromEmailVerificationInterval = setInterval(async () => {
      const nextEmail = await this.outlookService.getComposeFrom();

      if (this.destroyed || !nextEmail || firstEmail == nextEmail) return;

      this.storage.remove('saveOnlyEntries');
      this.saveToComponent()?.removeStorageItems();
      globalThis.location.href = globalThis.location.origin + '/home';
    }, this.intervalSearchForNewEmail);
  }

  private stopFromEmailVerification(): void {
    if (!this.fromEmailVerificationInterval) return;
    clearInterval(this.fromEmailVerificationInterval);
    this.fromEmailVerificationInterval = undefined;
    this.outlookStore.setIsComposeIntervalDefined(false);
  }
}
